import React, { useEffect, useState } from "react";
import { MessageCircle, Save, Loader2, Flame, Landmark } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { StaffSection } from "../components/StaffSection";

/**
 * Admin Settings → WhatsApp Business section.
 *
 * Lets David (or whoever) toggle the floating WhatsApp button on and off,
 * set the business number, business hours, days of the week, and the
 * fallback email used outside live-chat hours.
 *
 * Designed to work the moment a new eSIM number is activated — flip
 * `Enabled`, paste the number, save → button is live across the site.
 */
const DAYS = [
  { idx: 0, label: "Mon" },
  { idx: 1, label: "Tue" },
  { idx: 2, label: "Wed" },
  { idx: 3, label: "Thu" },
  { idx: 4, label: "Fri" },
  { idx: 5, label: "Sat" },
  { idx: 6, label: "Sun" },
];

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/admin/site-settings")
      .then((r) => setCfg(r.data))
      .catch(() => toast.error("Could not load settings"))
      .finally(() => setLoading(false));
    api.get("/products", { params: { only_visible: false } })
      .then((r) => setProducts(r.data))
      .catch(() => {});
  }, []);

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        whatsapp_enabled: !!cfg.whatsapp_enabled,
        whatsapp_number: cfg.whatsapp_number || "",
        whatsapp_offline_email: cfg.whatsapp_offline_email || "info@peptigenlabs.co.uk",
        whatsapp_prefilled_message: cfg.whatsapp_prefilled_message || "",
        whatsapp_hours_start: Number(cfg.whatsapp_hours_start ?? 9),
        whatsapp_hours_end: Number(cfg.whatsapp_hours_end ?? 18),
        whatsapp_days: cfg.whatsapp_days || [0, 1, 2, 3, 4],
        spotlight_sku: cfg.spotlight_sku || "PL-RET-30",
        bank_transfer_enabled: !!cfg.bank_transfer_enabled,
        bank_account_name: (cfg.bank_account_name || "").trim(),
        bank_sort_code: (cfg.bank_sort_code || "").trim(),
        bank_account_number: (cfg.bank_account_number || "").trim(),
      };
      const { data } = await api.patch("/admin/site-settings", payload);
      setCfg(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="text-[#9CA8B8] pg-mono">Loading settings…</div>;
  }

  const toggleDay = (idx) => {
    const current = new Set(cfg.whatsapp_days || []);
    if (current.has(idx)) current.delete(idx);
    else current.add(idx);
    update("whatsapp_days", Array.from(current).sort());
  };

  const sanitisedNumber = (cfg.whatsapp_number || "").replace(/[^0-9]/g, "");
  const previewHref = sanitisedNumber
    ? `https://wa.me/${sanitisedNumber}?text=${encodeURIComponent(cfg.whatsapp_prefilled_message || "")}`
    : null;

  return (
    <div data-testid="admin-settings">
      <div className="pg-micro mb-2">// Settings</div>
      <h1 className="text-[#F7FAFC] text-3xl pg-display mb-2" style={{ fontFamily: "'Exo 2'" }}>
        Site Settings
      </h1>
      <p className="text-[#9CA8B8] text-sm mb-8">
        Runtime configuration. Changes here apply across the live site within seconds — no redeploy needed.
      </p>

      <StaffSection />

      <section className="rounded-sm border border-[#1E2E44] bg-[#0F172A] overflow-hidden mb-6" data-testid="spotlight-section">
        <div className="px-5 py-4 border-b border-[#1E2E44] flex items-center gap-2.5">
          <Flame size={18} className="text-[#A6FF00]" />
          <h2 className="text-[#F7FAFC] pg-heading text-lg" style={{ fontFamily: "'Rajdhani'" }}>
            Homepage Flash-Sale Spotlight
          </h2>
        </div>
        <div className="px-5 py-5">
          <Field label="Featured product" hint="Shown in the big Flash Sale block on the homepage. Only renders while that product is on sale; set the sale price / end date on the product itself.">
            <select
              value={cfg.spotlight_sku || ""}
              onChange={(e) => update("spotlight_sku", e.target.value)}
              className="pg-input w-full"
              data-testid="spotlight-sku-select"
            >
              {products.map((p) => (
                <option key={p.sku} value={p.sku}>
                  {p.name} {p.strength} · {p.sku}{p.on_sale ? " · ON SALE" : " · not on sale"}{p.visible ? "" : " · hidden"}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      <section className="rounded-sm border border-[#1E2E44] bg-[#0F172A] overflow-hidden" data-testid="bank-transfer-section">
        <div className="px-5 py-4 border-b border-[#1E2E44] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Landmark size={18} className="text-[#A6FF00]" />
            <h2 className="text-[#F7FAFC] pg-heading text-lg" style={{ fontFamily: "'Rajdhani'" }}>
              Manual bank transfer details
            </h2>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="bank-transfer-enabled-toggle">
            <span className="text-[10px] pg-mono uppercase tracking-[0.18em] text-[#9CA8B8]">
              {cfg.bank_transfer_enabled ? "Shown in emails" : "Hidden"}
            </span>
            <input
              type="checkbox"
              checked={!!cfg.bank_transfer_enabled}
              onChange={(e) => update("bank_transfer_enabled", e.target.checked)}
              className="sr-only"
            />
            <span className={`w-10 h-5 rounded-full relative transition-colors ${cfg.bank_transfer_enabled ? "bg-[#A6FF00]" : "bg-[#1E2E44]"}`}>
              <span className={`absolute top-0.5 ${cfg.bank_transfer_enabled ? "left-[1.4rem]" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
            </span>
          </label>
        </div>
        <div className="px-5 py-5 grid gap-4 sm:grid-cols-3">
          <Field label="Account name">
            <input value={cfg.bank_account_name || ""} onChange={(e) => update("bank_account_name", e.target.value)} className="pg-input w-full" placeholder="PEPTIGENLABS LTD" data-testid="bank-account-name-input" />
          </Field>
          <Field label="Sort code">
            <input value={cfg.bank_sort_code || ""} onChange={(e) => update("bank_sort_code", e.target.value)} className="pg-input w-full" placeholder="00-00-00" data-testid="bank-sort-code-input" />
          </Field>
          <Field label="Account number">
            <input value={cfg.bank_account_number || ""} onChange={(e) => update("bank_account_number", e.target.value)} className="pg-input w-full" placeholder="12345678" data-testid="bank-account-number-input" />
          </Field>
          <div className="sm:col-span-3 text-[11px] text-[#9CA8B8] leading-snug">
            Used only in the automatic "payment didn't complete" emails. While this is <strong className="text-[#F7FAFC]">Hidden</strong> (or any field is blank) those emails say "reply to this email for bank details" instead of showing an account. Fena Pay-by-Bank at checkout is separate and unaffected.
          </div>
        </div>
      </section>

      <section className="rounded-sm border border-[#1E2E44] bg-[#0F172A] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#1E2E44] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <MessageCircle size={18} className="text-[#25D366]" />
            <h2 className="text-[#F7FAFC] pg-heading text-lg" style={{ fontFamily: "'Rajdhani'" }}>
              WhatsApp Business
            </h2>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="whatsapp-enabled-toggle">
            <span className="text-[10px] pg-mono uppercase tracking-[0.18em] text-[#9CA8B8]">
              {cfg.whatsapp_enabled ? "Live" : "Disabled"}
            </span>
            <input
              type="checkbox"
              checked={!!cfg.whatsapp_enabled}
              onChange={(e) => update("whatsapp_enabled", e.target.checked)}
              className="sr-only"
            />
            <span className={`w-10 h-5 rounded-full relative transition-colors ${cfg.whatsapp_enabled ? "bg-[#25D366]" : "bg-[#1E2E44]"}`}>
              <span className={`absolute top-0.5 ${cfg.whatsapp_enabled ? "left-[1.4rem]" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
            </span>
          </label>
        </div>

        <div className="px-5 py-5 space-y-5">
          <Field label="WhatsApp number" hint="International format, no leading +. e.g. 447XXXXXXXXX">
            <input
              type="text"
              value={cfg.whatsapp_number || ""}
              onChange={(e) => update("whatsapp_number", e.target.value)}
              placeholder="447XXXXXXXXX"
              className="pg-input w-full"
              data-testid="whatsapp-number-input"
            />
          </Field>

          <Field label="Pre-filled customer message" hint="What the customer sees pre-typed when they tap the button.">
            <textarea
              value={cfg.whatsapp_prefilled_message || ""}
              onChange={(e) => update("whatsapp_prefilled_message", e.target.value)}
              rows={2}
              className="pg-input w-full"
              data-testid="whatsapp-message-input"
            />
          </Field>

          <Field label="Offline fallback email" hint="Used outside business hours — the button switches to a mailto link.">
            <input
              type="email"
              value={cfg.whatsapp_offline_email || ""}
              onChange={(e) => update("whatsapp_offline_email", e.target.value)}
              placeholder="info@peptigenlabs.co.uk"
              className="pg-input w-full"
              data-testid="whatsapp-email-input"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Hours open (GMT)" hint="Start hour, inclusive">
              <input
                type="number"
                min={0}
                max={23}
                value={cfg.whatsapp_hours_start ?? 9}
                onChange={(e) => update("whatsapp_hours_start", Number(e.target.value))}
                className="pg-input w-full"
                data-testid="whatsapp-hours-start"
              />
            </Field>
            <Field label="Hours closed (GMT)" hint="End hour, exclusive (18 = up to 17:59)">
              <input
                type="number"
                min={0}
                max={24}
                value={cfg.whatsapp_hours_end ?? 18}
                onChange={(e) => update("whatsapp_hours_end", Number(e.target.value))}
                className="pg-input w-full"
                data-testid="whatsapp-hours-end"
              />
            </Field>
          </div>

          <Field label="Days of the week" hint="Toggle the days the button should be 'live'. Outside these days/hours it opens email instead.">
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => {
                const active = (cfg.whatsapp_days || []).includes(d.idx);
                return (
                  <button
                    key={d.idx}
                    type="button"
                    onClick={() => toggleDay(d.idx)}
                    className={`px-3 py-1.5 rounded-sm pg-mono text-[10px] uppercase tracking-[0.16em] border transition-colors ${active ? "bg-[#25D366]/15 border-[#25D366]/55 text-[#25D366]" : "bg-[#0B1220] border-[#1E2E44] text-[#9CA8B8] hover:border-[#5EEAD4]/40"}`}
                    data-testid={`whatsapp-day-${d.label.toLowerCase()}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </Field>

          {previewHref && (
            <div className="px-3 py-3 rounded-sm border border-[#1E2E44] bg-[#0B1220]">
              <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4] mb-1.5">
                // Preview link
              </div>
              <a href={previewHref} target="_blank" rel="noreferrer" className="text-[#5EEAD4] hover:text-[#A6FF00] text-[12px] break-all">
                {previewHref}
              </a>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#1E2E44] flex items-center justify-end gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="pg-btn-primary text-xs"
            data-testid="settings-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </section>

      <div className="mt-6 px-4 py-3 rounded-sm border border-[#5EEAD4]/25 bg-[#0B1220] text-[12px] text-[#9CA8B8] leading-relaxed">
        <span className="text-[#5EEAD4] pg-mono uppercase tracking-[0.18em] text-[10px]">// How it works</span>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>The floating button appears bottom-right on every page when <strong className="text-[#F7FAFC]">Enabled</strong> is on AND a number is set.</li>
          <li>During configured hours / days the button is <strong className="text-[#25D366]">green</strong> and opens WhatsApp.</li>
          <li>Outside those windows the button switches to <strong className="text-[#FF8A5B]">offline</strong> and opens a mailto link to your fallback email.</li>
          <li>Customers see a one-time popover after 8 seconds on their first visit, prompting them to chat / email.</li>
        </ul>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#9CA8B8] mb-1.5">
        {label}
      </div>
      {children}
      {hint && (
        <div className="text-[11px] text-[#9CA8B8] mt-1.5 leading-snug">{hint}</div>
      )}
    </label>
  );
}
